if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Label to restore ("Select a size") while an option is still unselected.
        this.awaitingSelectionLabel = this.awaitingSelection ? this.submitButtonText.textContent.trim() : null;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Also covers the sticky buy bar, which submits this form via its form attribute.
        if (this.awaitingSelection) {
          this.showSelectionError();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;

          // Toggle Klaviyo BIS trigger button
          if (text) {
            this.querySelector('.klaviyo-bis-trigger-button').classList.remove('hidden');
          }
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            this.awaitingSelection && this.awaitingSelectionLabel
              ? this.awaitingSelectionLabel
              : window.variantStrings.addToCart;

          // Toggle Klaviyo BIS trigger button
          this.querySelector('.klaviyo-bis-trigger-button').classList.add('hidden');
        }
      }

      get awaitingSelection() {
        return this.submitButton.dataset.awaitingSelection === 'true';
      }

      // Mirrors the server-rendered state of the freshly fetched submit button.
      setAwaitingSelection(isAwaiting, label) {
        if (isAwaiting) {
          this.submitButton.dataset.awaitingSelection = 'true';
          if (label) this.awaitingSelectionLabel = label;
        } else {
          delete this.submitButton.dataset.awaitingSelection;
          this.awaitingSelectionLabel = null;
          this.hideSelectionError();
        }
      }

      showSelectionError() {
        // Injecting into an already-present, never-display:none region is announced far more
        // reliably than un-hiding a region that already contains the text.
        const error = this.selectionError;
        if (error) error.textContent = error.dataset.selectionErrorMessage || '';

        const fieldset = this.sizeFieldset;
        if (!fieldset) return;

        fieldset.classList.add('size-selector--error');
        this.sizeInputs.forEach((input) => input.setAttribute('aria-invalid', 'true'));

        const { top, bottom } = fieldset.getBoundingClientRect();
        const isVisible = top >= 0 && bottom <= window.innerHeight;
        if (!isVisible) fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Move focus to the control that needs input. Programmatic focus does not check a radio,
        // and preventScroll keeps the smooth scroll above from being overridden by a jump.
        const inputs = this.sizeInputs;
        const target = inputs.find((input) => !input.classList.contains('disabled')) || inputs[0];
        target?.focus({ preventScroll: true });
      }

      hideSelectionError() {
        const error = this.selectionError;
        if (error) error.textContent = '';

        this.sizeFieldset?.classList.remove('size-selector--error');
        this.sizeInputs.forEach((input) => input.removeAttribute('aria-invalid'));
      }

      get selectionError() {
        return this.querySelector('.product-form__selection-error');
      }

      get sizeInputs() {
        return Array.from(this.sizeFieldset?.querySelectorAll('input[type="radio"]') || []);
      }

      // Re-queried each time: product-info.js replaces <variant-selects> on every option change.
      get sizeFieldset() {
        return this.closest('product-info')?.querySelector('[data-size-fieldset]');
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}